import {
  formatQueryForJsonApi,
  getPortalBaseUrl,
  isAnadoluPortalContext,
  JsonApiQueryParameters,
} from '@evolve/common-ui';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import {
  formatQueryParams,
  JsonApiResponseDto,
  parseJsonApiResponse,
  Response,
  TQueryParams,
} from 'evolve-ui';
export type TProfessionalArea = {
  id: string;
  name: string;
  categories: {
    id: string;
    name: string;
  }[];
};

export type TLocationArea = {
  id: string;
  nome: string;
  concelho: {
    id: string;
    nome: string;
  };
  distrito: {
    id: string;
    nome: string;
  };
};
export type TZipCodeLocation = {
  codigo_postal: {
    cpalf: string;
  };
  concelho: {
    nome: string;
  };
  distrito: {
    nome: string;
  };
  localidade: {
    nome: string;
  };
};

/** Same-origin on Anadolu portal (Apache proxies /api/locations); site.evolverh.pt elsewhere. */
export function resolveWebsiteApiBaseUrl(): string {
  const fromEnv = (process.env.GATSBY_WEBSITE_API_URL ?? '').replace(/\/$/, '');
  if (typeof window !== 'undefined' && isAnadoluPortalContext()) {
    return `${getPortalBaseUrl()}/api`;
  }
  return fromEnv;
}

const websiteBaseQuery: BaseQueryFn = (args, api, extraOptions) =>
  fetchBaseQuery({
    baseUrl: resolveWebsiteApiBaseUrl(),
    mode: 'cors',
  })(args, api, extraOptions);

export const websiteApi = createApi({
  reducerPath: 'website',
  baseQuery: websiteBaseQuery,
  tagTypes: ['ProfessionalAreas'],
  endpoints: (builder) => ({
    getProfessionalAreas: builder.query<TProfessionalArea[], void>({
      providesTags: ['ProfessionalAreas'],
      query: () => 'professional-areas',
      transformResponse: (res: any, meta, args) => {
        return res?.data?.attributes?.professionalAreas ?? [];
      },
    }),
    getLocationAreas: builder.query<TLocationArea[], void>({
      query: () => 'locations/distritos?populate=concelhos',
      transformResponse: (res: any, meta, args) => {
        return res?.data?.map((item: any) => item.attributes) ?? [];
      },
    }),
    getLocationsByZipCode: builder.query<
      Response<TZipCodeLocation>,
      { code: string; postalZone: string }
    >({
      query: ({ code, postalZone }) =>
        `locations/codigo-postal/${code}${postalZone}`,
      transformResponse: (res: any, meta, args) => {
        return parseJsonApiResponse<TZipCodeLocation>(
          res as JsonApiResponseDto<TZipCodeLocation>,
          meta,
          args,
          true,
        );
      },
    }),
    getWebsiteLocalidades: builder.query<Response<TLocationArea>, TQueryParams>(
      {
        query: (params) =>
          formatQueryParams(
            params,
            `${resolveWebsiteApiBaseUrl()}/locations/localidades`,
          ),
        transformResponse: (res: any, meta, args) => {
          return parseJsonApiResponse<TLocationArea>(
            res as JsonApiResponseDto<TLocationArea>,
            meta,
            args,
            true,
          );
        },
      },
    ),
    getWebsiteConcelhos: builder.query<
      Response<TLocationArea>,
      JsonApiQueryParameters
    >({
      query: (query) =>
        formatQueryForJsonApi(
          query,
          `${resolveWebsiteApiBaseUrl()}/locations/concelhos`,
        ),
      transformResponse: (res: any, meta, args) => {
        return parseJsonApiResponse<TLocationArea>(
          res as JsonApiResponseDto<TLocationArea>,
          meta,
          args,
          true,
        );
      },
    }),
  }),
});

export const {
  useGetProfessionalAreasQuery,
  useGetLocationAreasQuery,
  useGetLocationsByZipCodeQuery,
  useGetWebsiteLocalidadesQuery,
  useGetWebsiteConcelhosQuery,
} = websiteApi;
